import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Star, Heart, Briefcase, Activity, Sparkles, Calendar as CalendarIcon, Hash, Palette, Smile, ChevronLeft, ChevronRight, Info, AlertCircle, CheckCircle2, XCircle, List, ArrowRight } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
interface AstroDailyPageProps {
  onNavigate: (page: string) => void;
  initialTab?: 'horoscope' | 'moon' | 'calendar';
}
// --- HOROSCOPE DATA ---
const ZODIAC_SIGNS = [{
  id: 'aries',
  name: 'Овен',
  symbol: '♈',
  date: '21 мар - 19 апр',
  color: 'rose'
}, {
  id: 'taurus',
  name: 'Телец',
  symbol: '♉',
  date: '20 апр - 20 мая',
  color: 'emerald'
}, {
  id: 'gemini',
  name: 'Близнецы',
  symbol: '♊',
  date: '21 мая - 20 июн',
  color: 'amber'
}, {
  id: 'cancer',
  name: 'Рак',
  symbol: '♋',
  date: '21 июн - 22 июл',
  color: 'slate'
}, {
  id: 'leo',
  name: 'Лев',
  symbol: '♌',
  date: '23 июл - 22 авг',
  color: 'orange'
}, {
  id: 'virgo',
  name: 'Дева',
  symbol: '♍',
  date: '23 авг - 22 сен',
  color: 'teal'
}, {
  id: 'libra',
  name: 'Весы',
  symbol: '♎',
  date: '23 сен - 22 окт',
  color: 'pink'
}, {
  id: 'scorpio',
  name: 'Скорпион',
  symbol: '♏',
  date: '23 окт - 21 ноя',
  color: 'purple'
}, {
  id: 'sagittarius',
  name: 'Стрелец',
  symbol: '♐',
  date: '22 ноя - 21 дек',
  color: 'indigo'
}, {
  id: 'capricorn',
  name: 'Козерог',
  symbol: '♑',
  date: '22 дек - 19 янв',
  color: 'slate'
}, {
  id: 'aquarius',
  name: 'Водолей',
  symbol: '♒',
  date: '20 янв - 18 фев',
  color: 'cyan'
}, {
  id: 'pisces',
  name: 'Рыбы',
  symbol: '♓',
  date: '19 фев - 20 мар',
  color: 'sky'
}];
const SIGN_STYLES: Record<string, any> = {
  rose: {
    bg50: 'bg-rose-50',
    bg100: 'bg-rose-100',
    bg500: 'bg-rose-500',
    text500: 'text-rose-500',
    text700: 'text-rose-700',
    border500: 'border-rose-500'
  },
  emerald: {
    bg50: 'bg-emerald-50',
    bg100: 'bg-emerald-100',
    bg500: 'bg-emerald-500',
    text500: 'text-emerald-500',
    text700: 'text-emerald-700',
    border500: 'border-emerald-500'
  },
  amber: {
    bg50: 'bg-amber-50',
    bg100: 'bg-amber-100',
    bg500: 'bg-amber-500',
    text500: 'text-amber-500',
    text700: 'text-amber-700',
    border500: 'border-amber-500'
  },
  slate: {
    bg50: 'bg-slate-50',
    bg100: 'bg-slate-100',
    bg500: 'bg-slate-500',
    text500: 'text-slate-500',
    text700: 'text-slate-700',
    border500: 'border-slate-500'
  },
  orange: {
    bg50: 'bg-orange-50',
    bg100: 'bg-orange-100',
    bg500: 'bg-orange-500',
    text500: 'text-orange-500',
    text700: 'text-orange-700',
    border500: 'border-orange-500'
  },
  teal: {
    bg50: 'bg-teal-50',
    bg100: 'bg-teal-100',
    bg500: 'bg-teal-500',
    text500: 'text-teal-500',
    text700: 'text-teal-700',
    border500: 'border-teal-500'
  },
  pink: {
    bg50: 'bg-pink-50',
    bg100: 'bg-pink-100',
    bg500: 'bg-pink-500',
    text500: 'text-pink-500',
    text700: 'text-pink-700',
    border500: 'border-pink-500'
  },
  purple: {
    bg50: 'bg-purple-50',
    bg100: 'bg-purple-100',
    bg500: 'bg-purple-500',
    text500: 'text-purple-500',
    text700: 'text-purple-700',
    border500: 'border-purple-500'
  },
  indigo: {
    bg50: 'bg-indigo-50',
    bg100: 'bg-indigo-100',
    bg500: 'bg-indigo-500',
    text500: 'text-indigo-500',
    text700: 'text-indigo-700',
    border500: 'border-indigo-500'
  },
  cyan: {
    bg50: 'bg-cyan-50',
    bg100: 'bg-cyan-100',
    bg500: 'bg-cyan-500',
    text500: 'text-cyan-500',
    text700: 'text-cyan-700',
    border500: 'border-cyan-500'
  },
  sky: {
    bg50: 'bg-sky-50',
    bg100: 'bg-sky-100',
    bg500: 'bg-sky-500',
    text500: 'text-sky-500',
    text700: 'text-sky-700',
    border500: 'border-sky-500'
  }
};
const PERIODS = [{
  id: 'today',
  label: 'Сегодня'
}, {
  id: 'tomorrow',
  label: 'Завтра'
}, {
  id: 'week',
  label: 'Неделя'
}, {
  id: 'month',
  label: 'Месяц'
}];
const generateMockData = (signId: string, periodId: string) => {
  const hash = signId.charCodeAt(0) + signId.charCodeAt(signId.length - 1) + periodId.length;
  const forecasts = ['Сегодня отличный день для новых начинаний и смелых решений. Звезды благоприятствуют тем, кто готов взять инициативу в свои руки. Ваша интуиция будет особенно острой — прислушивайтесь к внутреннему голосу при принятии важных решений. Возможны неожиданные, но приятные новости издалека.', 'Энергия этого периода направлена на внутреннюю трансформацию. Отличное время для завершения старых дел и освобождения пространства для нового. В отношениях возможны глубокие разговоры, которые выведут вас на новый уровень понимания. Финансовые вопросы потребуют внимательности.', 'Ваша социальная активность на пике! Ожидайте интересных встреч и полезных знакомств. Звезды советуют быть открытыми к новым предложениям — одно из них может оказаться судьбоносным. В сфере здоровья обратите внимание на режим сна и питания.', 'Время сфокусироваться на карьерных целях и материальном благополучии. Ваш труд будет замечен и оценен по достоинству. В личной жизни возможны небольшие недопонимания, которые легко разрешатся, если вы проявите терпение и готовность к компромиссу.'];
  const numbers = ['7, 14, 21', '3, 9, 12', '5, 18, 22', '1, 8, 19', '4, 11, 27'];
  const colors = [{
    name: 'Изумрудный',
    bg: 'bg-emerald-500'
  }, {
    name: 'Индиго',
    bg: 'bg-indigo-500'
  }, {
    name: 'Коралловый',
    bg: 'bg-rose-400'
  }, {
    name: 'Золотой',
    bg: 'bg-amber-400'
  }, {
    name: 'Лазурный',
    bg: 'bg-sky-500'
  }];
  const moods = ['Вдохновленное', 'Спокойное', 'Энергичное', 'Задумчивое', 'Решительное'];
  return {
    forecast: forecasts[hash % forecasts.length],
    loveScore: 60 + hash % 40,
    careerScore: 50 + hash * 2 % 50,
    healthScore: 70 + hash * 3 % 30,
    number: numbers[hash % numbers.length],
    color: colors[hash % colors.length],
    mood: moods[hash % moods.length]
  };
};
const RatingBar = ({
  score,
  colorClass
}: {
  score: number;
  colorClass: string;
}) => <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mt-3" data-id="element-16655">
    <motion.div initial={{
    width: 0
  }} animate={{
    width: `${score}%`
  }} transition={{
    duration: 1,
    ease: 'easeOut'
  }} className={`h-full ${colorClass} rounded-full`} data-id="element-16656" />
  </div>;
// --- CALENDAR DATA ---
const formatMonthYear = (date: Date) => {
  const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
};
const getDaysInMonth = (year: number, month: number) => {
  const date = new Date(year, month, 1);
  const days = [];
  let firstDay = date.getDay() - 1;
  if (firstDay === -1) firstDay = 6;
  for (let i = 0; i < firstDay; i++) days.push(null);
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};
const currentTransits = [{
  planet: '☉ Солнце',
  sign: '♓ Рыбы',
  degree: "10°13'",
  element: 'water'
}, {
  planet: '☽ Луна',
  sign: '♒ Водолей',
  degree: "6°40'",
  element: 'air'
}, {
  planet: '☿ Меркурий',
  sign: '♓ Рыбы',
  degree: "22°02'",
  element: 'water'
}, {
  planet: '♀ Венера',
  sign: '♈ Овен',
  degree: "22°59'",
  element: 'fire'
}, {
  planet: '♂ Марс',
  sign: '♋ Рак',
  degree: "28°36'",
  element: 'water'
}, {
  planet: '♃ Юпитер',
  sign: '♋ Рак',
  degree: "15°15'",
  element: 'water'
}, {
  planet: '♄ Сатурн',
  sign: '♈ Овен',
  degree: "1°42'",
  element: 'fire'
}];
const elementColors: Record<string, string> = {
  fire: 'text-red-600',
  earth: 'text-emerald-600',
  air: 'text-indigo-600',
  water: 'text-sky-600'
};
const allEvents = [{
  id: 10,
  dateStr: '2024-09-02',
  displayDate: '2 Сентября',
  type: 'Новолуние',
  title: 'Новолуние в Деве',
  desc: 'Время для наведения порядка, планирования и внимания к здоровью.',
  icon: <Moon className="w-5 h-5" data-id="element-16657" />,
  color: 'text-emerald-500',
  bg: 'bg-emerald-100'
}, {
  id: 11,
  dateStr: '2024-09-18',
  displayDate: '18 Сентября',
  type: 'Затмение',
  title: 'Лунное затмение в Рыбах',
  desc: 'Завершение эмоциональных циклов, раскрытие тайн и интуитивных прозрений.',
  icon: <Moon className="w-5 h-5" data-id="element-16658" />,
  color: 'text-indigo-500',
  bg: 'bg-indigo-100'
}, {
  id: 1,
  dateStr: '2024-10-02',
  displayDate: '2 Октября',
  type: 'Затмение',
  title: 'Солнечное затмение в Весах',
  desc: 'Время кармических изменений в сфере партнерства и отношений.',
  icon: <Sun className="w-5 h-5" data-id="element-16659" />,
  color: 'text-amber-500',
  bg: 'bg-amber-100'
}, {
  id: 4,
  dateStr: '2024-10-09',
  displayDate: '9 Октября',
  type: 'Ретроград',
  title: 'Юпитер становится ретроградным',
  desc: 'Переоценка социальных связей, образования и мировоззрения.',
  icon: <AlertCircle className="w-5 h-5" data-id="element-16660" />,
  color: 'text-rose-500',
  bg: 'bg-rose-100'
}, {
  id: 5,
  dateStr: '2024-10-17',
  displayDate: '17 Октября',
  type: 'Полнолуние',
  title: 'Суперлуние в Овне',
  desc: 'Мощный всплеск энергии, кульминация личных проектов и инициатив.',
  icon: <Moon className="w-5 h-5" data-id="element-16661" />,
  color: 'text-rose-500',
  bg: 'bg-rose-100'
}, {
  id: 6,
  dateStr: '2024-11-01',
  displayDate: '1 Ноября',
  type: 'Новолуние',
  title: 'Новолуние в Скорпионе',
  desc: 'Глубокие трансформации, работа с подсознанием и финансами.',
  icon: <Moon className="w-5 h-5" data-id="element-16662" />,
  color: 'text-purple-500',
  bg: 'bg-purple-100'
}, {
  id: 7,
  dateStr: '2024-11-15',
  displayDate: '15 Ноября',
  type: 'Ингрессия',
  title: 'Сатурн выходит из ретрограда',
  desc: 'Ускорение в делах, связанных с карьерой, структурой и ответственностью.',
  icon: <Star className="w-5 h-5" data-id="element-16663" />,
  color: 'text-emerald-500',
  bg: 'bg-emerald-100'
}, {
  id: 8,
  dateStr: '2024-11-26',
  displayDate: '26 Ноября',
  type: 'Ретроград',
  title: 'Ретроградный Меркурий в Стрельце',
  desc: 'Задержки в путешествиях, обучении и юридических вопросах.',
  icon: <AlertCircle className="w-5 h-5" data-id="element-16664" />,
  color: 'text-rose-500',
  bg: 'bg-rose-100'
}, {
  id: 9,
  dateStr: '2024-12-06',
  displayDate: '6 Декабря',
  type: 'Ретроград',
  title: 'Ретроградный Марс во Льве',
  desc: 'Снижение энергии, пересмотр творческих проектов и способов самовыражения.',
  icon: <AlertCircle className="w-5 h-5" data-id="element-16665" />,
  color: 'text-rose-500',
  bg: 'bg-rose-100'
}, {
  id: 3,
  dateStr: '2024-12-15',
  displayDate: '15 Декабря',
  type: 'Ингрессия',
  title: 'Меркурий выходит из ретрограда',
  desc: 'Улучшение коммуникаций, зеленая улица для новых договоренностей.',
  icon: <Star className="w-5 h-5" data-id="element-16666" />,
  color: 'text-emerald-500',
  bg: 'bg-emerald-100'
}, {
  id: 12,
  dateStr: '2024-12-30',
  displayDate: '30 Декабря',
  type: 'Новолуние',
  title: 'Новолуние в Козероге',
  desc: 'Идеальное время для постановки долгосрочных целей на новый год.',
  icon: <Moon className="w-5 h-5" data-id="element-16667" />,
  color: 'text-slate-500',
  bg: 'bg-slate-100'
}];
export function AstroDailyPage({
  onNavigate,
  initialTab = 'horoscope'
}: AstroDailyPageProps) {
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    setActiveTab(initialTab);
    window.scrollTo(0, 0);
  }, [initialTab]);
  // Horoscope State
  const [activeSign, setActiveSign] = useState(ZODIAC_SIGNS[0].id);
  const [activePeriod, setActivePeriod] = useState('today');
  const zodiacScrollRef = useRef<HTMLDivElement>(null);
  const selectedSign = ZODIAC_SIGNS.find(s => s.id === activeSign) || ZODIAC_SIGNS[0];
  const signStyles = SIGN_STYLES[selectedSign.color] || SIGN_STYLES.slate;
  const horoscopeData = useMemo(() => generateMockData(activeSign, activePeriod), [activeSign, activePeriod]);
  const scrollZodiac = (direction: 'left' | 'right') => {
    if (zodiacScrollRef.current) {
      const scrollAmount = 200;
      zodiacScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  // Moon State
  const currentMoonDateStr = new Date().toLocaleDateString('ru-RU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  // Calendar State
  const [calendarView, setCalendarView] = useState<'month' | 'list'>('list');
  const [currentDate, setCurrentDate] = useState(new Date(2024, 9, 1)); // October 2024
  const [activeTooltipDay, setActiveTooltipDay] = useState<number | null>(null);
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const currentMonthEvents = useMemo(() => {
    return allEvents.filter(event => {
      const eventDate = new Date(event.dateStr);
      return eventDate.getMonth() === currentDate.getMonth() && eventDate.getFullYear() === currentDate.getFullYear();
    }).sort((a, b) => new Date(a.dateStr).getTime() - new Date(b.dateStr).getTime());
  }, [currentDate]);
  const calendarDays = useMemo(() => getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth()), [currentDate]);
  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  return <div className="min-h-screen bg-slate-50 pb-16 sm:pb-24" data-id="element-16668">
      {/* Shared Header */}
      <div className="bg-white border-b border-slate-200 pt-6 pb-0" data-id="element-16669">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-16670">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Астро-календарь'
        }]} className="mb-6" data-id="element-16671" />

          <div className="text-center max-w-2xl mx-auto mb-8" data-id="element-16672">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-id="element-16673">
              Астро-календарь
            </h1>
            <p className="text-base sm:text-lg text-slate-600 px-4 sm:px-0" data-id="element-16674">
              Ваш ежедневный путеводитель по звёздным ритмам, лунным циклам и
              важным астрологическим событиям.
            </p>
          </div>

          {/* Custom Inline Tabs */}
          <div className="flex justify-center mb-6" data-id="element-16675">
            <div className="bg-slate-100 p-1 rounded-xl flex w-full sm:w-auto" data-id="element-16676">
              <button onClick={() => setActiveTab('horoscope')} className={`flex items-center justify-center flex-1 sm:flex-none px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'horoscope' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} data-id="element-16677">
                <Sun className="w-4 h-4 mr-1.5 sm:mr-2 text-amber-500" data-id="element-16678" />{' '}
                Гороскоп
              </button>
              <button onClick={() => setActiveTab('moon')} className={`flex items-center justify-center flex-1 sm:flex-none px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'moon' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} data-id="element-16679">
                <Moon className="w-4 h-4 mr-1.5 sm:mr-2 text-indigo-500" data-id="element-16680" /> Луна
              </button>
              <button onClick={() => setActiveTab('calendar')} className={`flex items-center justify-center flex-1 sm:flex-none px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${activeTab === 'calendar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} data-id="element-16681">
                <CalendarIcon className="w-4 h-4 mr-1.5 sm:mr-2 text-emerald-500" data-id="element-16682" />{' '}
                Календарь
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-id="element-16683">
        {/* ==================== HOROSCOPE TAB ==================== */}
        {activeTab === 'horoscope' && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-16684">
            {/* Zodiac Selector */}
            <div className="relative -mx-4 sm:mx-0 mb-8" data-id="element-16685">
              <button onClick={() => scrollZodiac('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-all lg:hidden" data-id="element-16686">
                <ChevronLeft className="w-4 h-4" data-id="element-16687" />
              </button>

              <div ref={zodiacScrollRef} className="flex overflow-x-auto hide-scrollbar gap-1 sm:gap-1.5 pb-2 px-8 sm:px-0 lg:px-0 snap-x scroll-smooth" data-id="element-16688">
                {ZODIAC_SIGNS.map(sign => {
              const sStyle = SIGN_STYLES[sign.color] || SIGN_STYLES.slate;
              const isActive = activeSign === sign.id;
              return <button key={sign.id} onClick={() => setActiveSign(sign.id)} className={`flex flex-col items-center min-w-[64px] sm:min-w-[72px] lg:min-w-0 lg:flex-1 py-2 px-2 sm:px-3 rounded-xl transition-all snap-center shrink-0 ${isActive ? `${sStyle.bg50} border ${sStyle.border500} shadow-sm` : 'bg-white border border-transparent hover:bg-slate-50 hover:border-slate-200'}`} data-id="element-16689">
                      <span className="text-xl sm:text-2xl mb-0.5 sm:mb-1" data-id="element-16690">
                        {sign.symbol}
                      </span>
                      <span className={`text-[11px] sm:text-xs font-bold leading-tight ${isActive ? sStyle.text700 : 'text-slate-700'}`} data-id="element-16691">
                        {sign.name}
                      </span>
                      <span className="text-[8px] sm:text-[9px] text-slate-400 mt-0.5 whitespace-nowrap" data-id="element-16692">
                        {sign.date}
                      </span>
                    </button>;
            })}
              </div>

              <button onClick={() => scrollZodiac('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 shadow-md border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-white hover:text-slate-900 transition-all lg:hidden" data-id="element-16693">
                <ChevronRight className="w-4 h-4" data-id="element-16694" />
              </button>
              <div className="absolute top-0 left-8 w-4 h-full bg-gradient-to-r from-white to-transparent pointer-events-none lg:hidden" data-id="element-16695" />
              <div className="absolute top-0 right-8 w-4 h-full bg-gradient-to-l from-white to-transparent pointer-events-none lg:hidden" data-id="element-16696" />
            </div>

            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 sm:mb-8 gap-4" data-id="element-16697">
              <div className="flex items-center gap-4" data-id="element-16698">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full ${signStyles.bg100} flex items-center justify-center text-2xl sm:text-3xl shadow-inner shrink-0`} data-id="element-16699">
                  {selectedSign.symbol}
                </div>
                <div data-id="element-16700">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-16701">
                    {selectedSign.name}
                  </h2>
                  <p className="text-sm sm:text-base text-slate-500" data-id="element-16702">
                    {selectedSign.date}
                  </p>
                </div>
              </div>

              <div className="bg-white p-1 rounded-xl shadow-sm border border-slate-200 flex overflow-x-auto hide-scrollbar" data-id="element-16703">
                {PERIODS.map(period => <button key={period.id} onClick={() => setActivePeriod(period.id)} className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${activePeriod === period.id ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} data-id="element-16704">
                    {period.label}
                  </button>)}
              </div>
            </div>

            <Card className="mb-6 sm:mb-8 overflow-hidden border-0 shadow-md" data-id="element-16705">
              <div className={`h-1.5 sm:h-2 ${signStyles.bg500} w-full`} data-id="element-16706" />
              <div className="p-5 sm:p-8" data-id="element-16707">
                <div className="flex items-center gap-2 mb-3 sm:mb-4" data-id="element-16708">
                  <Sparkles className={`w-5 h-5 ${signStyles.text500}`} data-id="element-16709" />
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900" data-id="element-16710">
                    Общий прогноз
                  </h3>
                </div>
                <p className="text-slate-700 leading-relaxed text-base sm:text-lg" data-id="element-16711">
                  {horoscopeData.forecast}
                </p>
              </div>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-16712">
              <Card padding="md" className="border-rose-100 bg-rose-50/30 hover:shadow-md transition-shadow flex flex-col" data-id="element-16713">
                <div className="flex items-center justify-between mb-3" data-id="element-16714">
                  <div className="flex items-center gap-2" data-id="element-16715">
                    <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-16716">
                      <Heart className="w-4 h-4 text-rose-600" data-id="element-16717" />
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-16718">Любовь</h4>
                  </div>
                  <span className="text-sm font-bold text-rose-600" data-id="element-16719">
                    {horoscopeData.loveScore}%
                  </span>
                </div>
                <p className="text-sm text-slate-600 flex-grow" data-id="element-16720">
                  Романтические энергии на подъеме. Отличное время для
                  откровенных разговоров с партнером или новых знакомств.
                </p>
                <RatingBar score={horoscopeData.loveScore} colorClass="bg-rose-500" data-id="element-16721" />
              </Card>

              <Card padding="md" className="border-indigo-100 bg-indigo-50/30 hover:shadow-md transition-shadow flex flex-col" data-id="element-16722">
                <div className="flex items-center justify-between mb-3" data-id="element-16723">
                  <div className="flex items-center gap-2" data-id="element-16724">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center" data-id="element-16725">
                      <Briefcase className="w-4 h-4 text-indigo-600" data-id="element-16726" />
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-16727">Карьера</h4>
                  </div>
                  <span className="text-sm font-bold text-indigo-600" data-id="element-16728">
                    {horoscopeData.careerScore}%
                  </span>
                </div>
                <p className="text-sm text-slate-600 flex-grow" data-id="element-16729">
                  Проявите лидерские качества. Ваши идеи будут услышаны
                  руководством. Возможны небольшие финансовые поступления.
                </p>
                <RatingBar score={horoscopeData.careerScore} colorClass="bg-indigo-500" data-id="element-16730" />
              </Card>

              <Card padding="md" className="border-emerald-100 bg-emerald-50/30 hover:shadow-md transition-shadow flex flex-col sm:col-span-2 lg:col-span-1" data-id="element-16731">
                <div className="flex items-center justify-between mb-3" data-id="element-16732">
                  <div className="flex items-center gap-2" data-id="element-16733">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-16734">
                      <Activity className="w-4 h-4 text-emerald-600" data-id="element-16735" />
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-16736">Здоровье</h4>
                  </div>
                  <span className="text-sm font-bold text-emerald-600" data-id="element-16737">
                    {horoscopeData.healthScore}%
                  </span>
                </div>
                <p className="text-sm text-slate-600 flex-grow" data-id="element-16738">
                  Энергетический потенциал высок, но не забывайте об отдыхе.
                  Вечерняя прогулка поможет снять напряжение дня.
                </p>
                <RatingBar score={horoscopeData.healthScore} colorClass="bg-emerald-500" data-id="element-16739" />
              </Card>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 shadow-sm" data-id="element-16740">
              <div className="flex items-center gap-3 p-3 sm:p-0 rounded-xl sm:rounded-none bg-slate-50 sm:bg-transparent border border-slate-100 sm:border-none" data-id="element-16741">
                <div className="w-10 h-10 rounded-full bg-white sm:bg-slate-100 flex items-center justify-center shadow-sm sm:shadow-none shrink-0" data-id="element-16742">
                  <Hash className="w-5 h-5 text-slate-500" data-id="element-16743" />
                </div>
                <div data-id="element-16744">
                  <div className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-16745">
                    Число дня
                  </div>
                  <div className="text-base sm:text-lg font-bold text-slate-900" data-id="element-16746">
                    {horoscopeData.number}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 sm:p-0 rounded-xl sm:rounded-none bg-slate-50 sm:bg-transparent border border-slate-100 sm:border-none" data-id="element-16747">
                <div className="w-10 h-10 rounded-full bg-white sm:bg-slate-100 flex items-center justify-center shadow-sm sm:shadow-none shrink-0" data-id="element-16748">
                  <Palette className="w-5 h-5 text-slate-500" data-id="element-16749" />
                </div>
                <div data-id="element-16750">
                  <div className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-16751">
                    Цвет удачи
                  </div>
                  <div className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-16752">
                    <span className={`w-3 h-3 rounded-full ${horoscopeData.color.bg}`} data-id="element-16753"></span>
                    {horoscopeData.color.name}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 sm:p-0 rounded-xl sm:rounded-none bg-slate-50 sm:bg-transparent border border-slate-100 sm:border-none" data-id="element-16754">
                <div className="w-10 h-10 rounded-full bg-white sm:bg-slate-100 flex items-center justify-center shadow-sm sm:shadow-none shrink-0" data-id="element-16755">
                  <Smile className="w-5 h-5 text-slate-500" data-id="element-16756" />
                </div>
                <div data-id="element-16757">
                  <div className="text-[10px] sm:text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-16758">
                    Настроение
                  </div>
                  <div className="text-base sm:text-lg font-bold text-slate-900" data-id="element-16759">
                    {horoscopeData.mood}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}

        {/* ==================== MOON TAB ==================== */}
        {activeTab === 'moon' && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-16760">
            <div className="bg-slate-900 text-white p-8 sm:p-10 mb-8 relative overflow-hidden rounded-2xl shadow-xl" data-id="element-16761">
              <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-16762" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-16763" />
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-16764">
                <div className="max-w-2xl" data-id="element-16765">
                  <Badge variant="pro" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 mb-4" data-id="element-16766">
                    Обновляется ежедневно
                  </Badge>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-id="element-16767">
                    Лунный календарь
                  </h2>
                  <p className="text-xl text-slate-300 mb-2 capitalize" data-id="element-16768">
                    {currentMoonDateStr}
                  </p>
                  <p className="text-slate-400" data-id="element-16769">
                    Следите за фазами Луны, чтобы планировать важные дела,
                    стрижки, посадки и понимать общий энергетический фон дня.
                  </p>
                </div>
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 120,
              repeat: Infinity,
              ease: 'linear'
            }} className="w-40 h-40 sm:w-48 sm:h-48 rounded-full bg-gradient-to-br from-slate-100 to-slate-400 shadow-[0_0_50px_rgba(255,255,255,0.2)] flex items-center justify-center relative overflow-hidden shrink-0" data-id="element-16770">
                  <div className="absolute inset-0 bg-slate-900 rounded-full transform translate-x-1/4 scale-110 opacity-80 mix-blend-multiply" data-id="element-16771" />
                  <div className="absolute inset-0 shadow-inner rounded-full" data-id="element-16772" />
                </motion.div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-16773">
              <div className="lg:col-span-2 space-y-8" data-id="element-16774">
                <Card className="p-6 sm:p-8 shadow-xl border-0" data-id="element-16775">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-16776">
                    Энергия сегодняшнего дня
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" data-id="element-16777">
                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-16778">
                      <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-16779">
                        Фаза Луны
                      </div>
                      <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-16780">
                        Растущая Луна{' '}
                        <span className="text-indigo-500" data-id="element-16781">🌔</span>
                      </div>
                      <div className="text-sm text-slate-600 mt-2" data-id="element-16782">
                        Освещенность: 78%
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-16783">
                      <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-16784">
                        Лунный день
                      </div>
                      <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-16785">
                        11-й лунный день
                      </div>
                      <div className="text-sm text-slate-600 mt-2" data-id="element-16786">
                        Символ: Огненный меч
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-16787">
                      <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-16788">
                        Луна в знаке
                      </div>
                      <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-16789">
                        Лев <span className="text-amber-500" data-id="element-16790">♌</span>
                      </div>
                      <div className="text-sm text-slate-600 mt-2" data-id="element-16791">
                        Стихия: Огонь
                      </div>
                    </div>
                    <div className="bg-rose-50 rounded-2xl p-5 border border-rose-100" data-id="element-16792">
                      <div className="text-sm text-rose-600 font-medium mb-1 flex items-center gap-1" data-id="element-16793">
                        <AlertCircle className="w-4 h-4" data-id="element-16794" /> Внимание
                      </div>
                      <div className="text-xl font-bold text-slate-900" data-id="element-16795">
                        Луна без курса
                      </div>
                      <div className="text-sm text-slate-600 mt-2" data-id="element-16796">
                        С 14:30 до 17:45 (МСК)
                      </div>
                    </div>
                  </div>
                  <div className="prose prose-slate max-w-none" data-id="element-16797">
                    <p data-id="element-16798">
                      Один из самых энергетически мощных дней лунного месяца.
                      Энергия бьет ключом, пробуждаются скрытые резервы
                      организма. Отличный день для активных действий, начала
                      новых проектов и физических нагрузок.
                    </p>
                    <p data-id="element-16799">
                      Луна во Льве добавляет желания быть в центре внимания,
                      творить и радовать себя. Однако в период "Луны без курса"
                      (с 14:30 до 17:45) лучше воздержаться от принятия важных
                      решений и подписания документов — дела, начатые в это
                      время, могут пойти не по плану.
                    </p>
                  </div>
                </Card>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-16800">
                  <Card className="p-6 border-emerald-200 bg-emerald-50/30" data-id="element-16801">
                    <div className="flex items-center gap-2 mb-4" data-id="element-16802">
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-16803" />
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-16804">
                        Благоприятно
                      </h3>
                    </div>
                    <ul className="space-y-3" data-id="element-16805">
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-16806">
                        <span className="text-emerald-500 mt-0.5" data-id="element-16807">•</span>{' '}
                        Творческие начинания
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-16808">
                        <span className="text-emerald-500 mt-0.5" data-id="element-16809">•</span>{' '}
                        Физическая активность и спорт
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-16810">
                        <span className="text-emerald-500 mt-0.5" data-id="element-16811">•</span>{' '}
                        Стрижка волос (для быстрого роста)
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-16812">
                        <span className="text-emerald-500 mt-0.5" data-id="element-16813">•</span>{' '}
                        Покупки одежды и украшений
                      </li>
                    </ul>
                  </Card>
                  <Card className="p-6 border-rose-200 bg-rose-50/30" data-id="element-16814">
                    <div className="flex items-center gap-2 mb-4" data-id="element-16815">
                      <XCircle className="w-6 h-6 text-rose-500" data-id="element-16816" />
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-16817">
                        Не рекомендуется
                      </h3>
                    </div>
                    <ul className="space-y-3" data-id="element-16818">
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-16819">
                        <span className="text-rose-500 mt-0.5" data-id="element-16820">•</span>{' '}
                        Пассивность и лень
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-16821">
                        <span className="text-rose-500 mt-0.5" data-id="element-16822">•</span>{' '}
                        Хирургические операции на сердце
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-16823">
                        <span className="text-rose-500 mt-0.5" data-id="element-16824">•</span> Важные
                        дела в период "Луны без курса"
                      </li>
                      <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-16825">
                        <span className="text-rose-500 mt-0.5" data-id="element-16826">•</span>{' '}
                        Конфликты с начальством
                      </li>
                    </ul>
                  </Card>
                </div>
              </div>

              <div className="space-y-6" data-id="element-16827">
                <Card className="p-6" data-id="element-16828">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-16829">
                    <CalendarIcon className="w-5 h-5 text-indigo-500" data-id="element-16830" />{' '}
                    Ближайшие фазы
                  </h3>
                  <div className="space-y-4" data-id="element-16831">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-16832">
                      <div className="flex items-center gap-3" data-id="element-16833">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-lg" data-id="element-16834">
                          🌕
                        </div>
                        <div data-id="element-16835">
                          <div className="text-sm font-bold text-slate-900" data-id="element-16836">
                            Полнолуние
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-16837">в Деве</div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-slate-700" data-id="element-16838">
                        24 фев
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-16839">
                      <div className="flex items-center gap-3" data-id="element-16840">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-lg" data-id="element-16841">
                          🌗
                        </div>
                        <div data-id="element-16842">
                          <div className="text-sm font-bold text-slate-900" data-id="element-16843">
                            Третья четверть
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-16844">
                            в Стрельце
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-slate-700" data-id="element-16845">
                        3 мар
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-16846">
                      <div className="flex items-center gap-3" data-id="element-16847">
                        <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-lg" data-id="element-16848">
                          🌑
                        </div>
                        <div data-id="element-16849">
                          <div className="text-sm font-bold text-slate-900" data-id="element-16850">
                            Новолуние
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-16851">в Рыбах</div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-slate-700" data-id="element-16852">
                        10 мар
                      </div>
                    </div>
                  </div>
                </Card>
                <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100" data-id="element-16853">
                  <div className="flex items-start gap-3" data-id="element-16854">
                    <Info className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" data-id="element-16855" />
                    <div data-id="element-16856">
                      <h4 className="font-bold text-indigo-900 mb-2" data-id="element-16857">
                        Что такое Луна без курса?
                      </h4>
                      <p className="text-sm text-indigo-800/80 leading-relaxed" data-id="element-16858">
                        Это период, когда Луна сделала свой последний мажорный
                        аспект в одном знаке зодиака, но еще не перешла в
                        следующий. В это время дела часто идут впустую, планы
                        срываются, а начатые проекты не приносят ожидаемого
                        результата.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}

        {/* ==================== CALENDAR TAB ==================== */}
        {activeTab === 'calendar' && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-16859">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8" data-id="element-16860">
              <div className="flex items-center flex-wrap bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border border-indigo-100" data-id="element-16861">
                <Moon className="w-4 h-4 mr-1.5 sm:mr-2 text-indigo-500" data-id="element-16862" />
                <span data-id="element-16863">Луна без курса:</span>
                <span className="ml-1.5 sm:ml-2 font-bold" data-id="element-16864">14:56 - 18:30</span>
                <div className="group relative ml-2 cursor-help" data-id="element-16865">
                  <Info className="w-4 h-4 text-indigo-400" data-id="element-16866" />
                  <div className="absolute bottom-full right-0 sm:left-1/2 sm:transform sm:-translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-16867">
                    Период неэффективности. Не рекомендуется начинать новые
                    важные дела.
                  </div>
                </div>
              </div>

              <div className="flex bg-slate-100 p-1 rounded-xl w-fit" data-id="element-16868">
                <button onClick={() => setCalendarView('month')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${calendarView === 'month' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-16869">
                  <CalendarIcon className="w-4 h-4 mr-2" data-id="element-16870" /> Месяц
                </button>
                <button onClick={() => setCalendarView('list')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${calendarView === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-16871">
                  <List className="w-4 h-4 mr-2" data-id="element-16872" /> Список
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-10" data-id="element-16873">
              <Card padding="md" className="border-amber-200 bg-amber-50/50 lg:col-span-1 self-start" data-id="element-16874">
                <div className="flex items-start gap-3" data-id="element-16875">
                  <div className="p-2 rounded-xl bg-amber-100 text-amber-600" data-id="element-16876">
                    <Moon className="w-5 h-5" data-id="element-16877" />
                  </div>
                  <div data-id="element-16878">
                    <div className="font-bold text-slate-900 mb-1" data-id="element-16879">
                      Луна без курса
                    </div>
                    <div className="text-sm text-slate-700 mb-2" data-id="element-16880">
                      ☽ Луна в Водолее • до{' '}
                      <span className="font-semibold" data-id="element-16881">14:56 МСК</span>
                    </div>
                    <div className="flex items-start gap-1.5 text-xs text-slate-500" data-id="element-16882">
                      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-16883" />
                      <span data-id="element-16884">
                        Не рекомендуется начинать важные дела, подписывать
                        документы и делать крупные покупки.
                      </span>
                    </div>
                  </div>
                </div>
              </Card>

              <Card padding="none" className="lg:col-span-2 overflow-hidden" data-id="element-16885">
                <div className="px-4 py-3 bg-slate-50/80 border-b border-slate-100 flex items-center justify-between" data-id="element-16886">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-16887">
                    Текущие транзиты
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-16888">
                    Сегодня, 22:46 МСК
                  </div>
                </div>
                <div className="divide-y divide-slate-100" data-id="element-16889">
                  {currentTransits.map((t, i) => <div key={i} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/50 transition-colors" data-id="element-16890">
                      <span className="text-xs sm:text-sm text-slate-500 w-24 sm:w-28 flex-shrink-0" data-id="element-16891">
                        {t.planet}
                      </span>
                      <span className={`font-bold text-xs sm:text-sm ${elementColors[t.element]} flex-1 text-center`} data-id="element-16892">
                        {t.sign}
                      </span>
                      <span className="text-[10px] sm:text-xs text-slate-400 font-mono w-14 sm:w-16 text-right" data-id="element-16893">
                        {t.degree}
                      </span>
                    </div>)}
                </div>
              </Card>
            </div>

            <div className="flex items-center justify-between mb-4 sm:mb-8" data-id="element-16894">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 capitalize" data-id="element-16895">
                {formatMonthYear(currentDate)}
              </h2>
              <div className="flex space-x-2" data-id="element-16896">
                <Button variant="outline" size="sm" icon={<ChevronLeft className="w-4 h-4" data-id="element-16898" />} onClick={prevMonth} data-id="element-16897" />
                <Button variant="outline" size="sm" icon={<ChevronRight className="w-4 h-4" data-id="element-16900" />} onClick={nextMonth} data-id="element-16899" />
              </div>
            </div>

            {calendarView === 'list' ? <div className="space-y-4" data-id="element-16901">
                {currentMonthEvents.length > 0 ? currentMonthEvents.map(event => <Card key={event.id} hoverable padding="lg" className="flex flex-col sm:flex-row gap-6 cursor-pointer" onClick={() => onNavigate('calendar-event')} data-id="element-16902">
                      <div className="flex-shrink-0 text-center sm:text-left sm:w-32" data-id="element-16903">
                        <div className="text-sm text-slate-500 font-medium" data-id="element-16904">
                          {event.displayDate.split(' ')[1]}
                        </div>
                        <div className="text-3xl font-bold text-slate-900" data-id="element-16905">
                          {event.displayDate.split(' ')[0]}
                        </div>
                      </div>
                      <div className="flex-grow" data-id="element-16906">
                        <div className="flex items-center gap-3 mb-2" data-id="element-16907">
                          <div className={`p-1.5 rounded-lg ${event.bg} ${event.color}`} data-id="element-16908">
                            {event.icon}
                          </div>
                          <Badge variant="neutral" data-id="element-16909">{event.type}</Badge>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-16910">
                          {event.title}
                        </h3>
                        <p className="text-slate-600" data-id="element-16911">{event.desc}</p>
                      </div>
                      <div className="flex-shrink-0 flex items-center justify-center sm:justify-end" data-id="element-16912">
                        <Button variant="ghost" className="hidden sm:flex" onClick={e => {
                e.stopPropagation();
                onNavigate('calendar-event');
              }} data-id="element-16913">
                          Подробнее
                        </Button>
                      </div>
                    </Card>) : <Card padding="xl" className="text-center text-slate-500" data-id="element-16914">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" data-id="element-16915" />
                    <p className="text-lg font-medium text-slate-900 mb-2" data-id="element-16916">
                      Нет важных событий
                    </p>
                    <p data-id="element-16917">В этом месяце нет значимых астрологических событий.</p>
                  </Card>}
              </div> : <Card padding="none" className="overflow-hidden overflow-y-visible" onClick={() => setActiveTooltipDay(null)} data-id="element-16918">
                <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200" data-id="element-16919">
                  {weekDays.map((day, i) => <div key={i} className="py-2 sm:py-3 text-center text-[11px] sm:text-sm font-semibold text-slate-600" data-id="element-16920">
                      {day}
                    </div>)}
                </div>
                <div className="grid grid-cols-7 auto-rows-[56px] sm:auto-rows-[100px] md:auto-rows-[120px] divide-x divide-y divide-slate-100" data-id="element-16921">
                  {calendarDays.map((date, i) => {
              if (!date) return <div key={`empty-${i}`} className="bg-slate-50/50" data-id="element-16922"></div>;
              const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              const dayEvents = currentMonthEvents.filter(e => e.dateStr === dateStr);
              const isToday = new Date().toDateString() === date.toDateString();
              return <div key={i} className={`group/day p-1 sm:p-2 relative transition-colors ${dayEvents.length > 0 ? 'hover:bg-slate-50 cursor-pointer' : ''} ${isToday ? 'bg-indigo-50/30' : ''}`} onClick={e => {
                if (dayEvents.length > 0) {
                  e.stopPropagation();
                  setActiveTooltipDay(activeTooltipDay === i ? null : i);
                }
              }} data-id="element-16923">
                        <div className={`text-xs sm:text-sm font-medium w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full mb-0.5 sm:mb-1 ${isToday ? 'bg-indigo-600 text-white' : 'text-slate-700'}`} data-id="element-16924">
                          {date.getDate()}
                        </div>
                        <div className="flex gap-0.5 flex-wrap sm:hidden" data-id="element-16925">
                          {dayEvents.map(event => <div key={event.id} className={`w-1.5 h-1.5 rounded-full ${event.bg.replace('100', '400')}`} data-id="element-16926" />)}
                        </div>
                        <div className="hidden sm:block space-y-1 overflow-y-auto hide-scrollbar max-h-[60px] md:max-h-[80px]" data-id="element-16927">
                          {dayEvents.map(event => <div key={event.id} className={`text-[10px] md:text-xs px-1.5 py-0.5 md:py-1 rounded truncate ${event.bg} ${event.color} font-medium`} title={event.title} data-id="element-16928">
                              {event.title}
                            </div>)}
                        </div>

                        {/* Tap/hover tooltip for days with events */}
                        {dayEvents.length > 0 && activeTooltipDay === i && <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-30 animate-in fade-in duration-150" onClick={e => e.stopPropagation()} data-id="element-16929">
                            <div className="bg-slate-900 text-white text-[11px] sm:text-xs rounded-xl shadow-xl px-3 py-2 min-w-[140px] max-w-[200px]" data-id="element-16930">
                              {dayEvents.map((event, idx) => <div key={event.id} className={`${idx > 0 ? 'mt-1.5 pt-1.5 border-t border-slate-700' : ''}`} data-id="element-16931">
                                  <div className="font-medium truncate" data-id="element-16932">
                                    {event.title}
                                  </div>
                                  <div className="text-slate-400 text-[10px] mt-0.5" data-id="element-16933">
                                    {event.type}
                                  </div>
                                </div>)}
                              <button onClick={e => {
                      e.stopPropagation();
                      setActiveTooltipDay(null);
                      onNavigate('calendar-event');
                    }} className="mt-2 pt-1.5 border-t border-slate-700 text-indigo-300 hover:text-indigo-200 font-medium text-[11px] flex items-center gap-1 w-full transition-colors" data-id="element-16934">
                                Подробнее <ArrowRight className="w-3 h-3" data-id="element-16935" />
                              </button>
                              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-slate-900" data-id="element-16936" />
                            </div>
                          </div>}
                      </div>;
            })}
                </div>
              </Card>}
          </motion.div>}

        {/* ==================== SHARED CTA ==================== */}
        <div className="mt-12 sm:mt-16 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-3xl p-6 sm:p-12 text-center relative overflow-hidden shadow-xl" data-id="element-16937">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-16938" />
          <div className="relative z-10" data-id="element-16939">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4" data-id="element-16940">
              Хотите персональный прогноз?
            </h2>
            <p className="text-sm sm:text-base text-indigo-200 mb-6 sm:mb-8 max-w-xl mx-auto" data-id="element-16941">
              Общий гороскоп — это только начало. Закажите индивидуальный
              прогноз транзитов, построенный по вашей натальной карте.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center" data-id="element-16942">
              <button onClick={() => onNavigate('service-forecast')} className="px-6 sm:px-8 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors text-sm sm:text-base" data-id="element-16943">
                Заказать прогноз транзитов
              </button>
              <button onClick={() => onNavigate('free-chart')} className="px-6 sm:px-8 py-3 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/10 transition-colors text-sm sm:text-base" data-id="element-16944">
                Бесплатная карта
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}